import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import evidenceService from '../services/evidenceService';
import EncryptedFileViewer from '../components/EncryptedFileViewer';
import EvidenceViewer from '../components/EvidenceViewer';

function Verification() {
  const { code } = useParams();
  const [verificationCode, setVerificationCode] = useState(code || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!verificationCode.trim()) return;

    setLoading(true);
    try {
      const verifyResult = await evidenceService.verifyEvidence(verificationCode);
      setResult(verifyResult);
    } catch (error) {
      setResult({
        verified: false,
        message: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Extract verification code from either record or input
  const displayVerificationCode = result?.record?.verificationCode || verificationCode;

  return (
    <div className="page-container">
      <h1>Verify Evidence</h1>
      <p>Enter verification code to check evidence authenticity</p>

      <div className="card mt-4">
        <div className="form-group">
          <label className="form-label">Verification Code</label>
          <input
            type="text"
            className="form-input"
            placeholder="ABC-DEF-GHI"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
          />
        </div>

        <button
          className="btn btn-primary"
          onClick={handleVerify}
          disabled={loading || !verificationCode.trim()}
        >
          {loading ? 'Verifying...' : 'Verify Evidence'}
        </button>

        {result && result.verified && (
          <>
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(5, 150, 105, 0.2)'
            }}>
              <h2 style={{
                margin: 0,
                color: 'white',
                fontSize: '1.75rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '2rem' }}>✓</span>
                Evidence Verified
              </h2>
            </div>

            <div style={{
              marginTop: '1.5rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1.5rem'
            }}>
              {/* Verification Details Column */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}>
                <h3 style={{
                  margin: '0 0 1.5rem 0',
                  fontSize: '1.25rem',
                  color: '#1f2937',
                  fontWeight: 600,
                  paddingBottom: '0.75rem',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  Verification Details
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                      File Name
                    </label>
                    <div style={{ fontSize: '0.95rem', color: '#1f2937', fontWeight: 500 }}>
                      {result.details?.fileName}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                      Verification Code
                    </label>
                    <div style={{
                      fontFamily: 'monospace',
                      background: '#f3f4f6',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      color: '#1f2937',
                      fontWeight: 600,
                      border: '1px solid #d1d5db'
                    }}>
                      {displayVerificationCode}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                      Upload Date
                    </label>
                    <div style={{ fontSize: '0.95rem', color: '#1f2937' }}>
                      {result.details?.uploadDate}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                      File Size
                    </label>
                    <div style={{ fontSize: '0.95rem', color: '#1f2937' }}>
                      {result.details?.fileSize}
                    </div>
                  </div>

                  {result.details?.fingerprint && (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                        File Hash (SHA-256)
                      </label>
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        color: '#4b5563',
                        wordBreak: 'break-all',
                        background: '#f9fafb',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb'
                      }}>
                        {result.details.fingerprint}
                      </div>
                    </div>
                  )}

                  {result.details?.caseNumber && (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                        Case Number
                      </label>
                      <div style={{ fontSize: '0.95rem', color: '#1f2937' }}>
                        {result.details.caseNumber}
                      </div>
                    </div>
                  )}

                  {result.details?.description && (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                        Description
                      </label>
                      <div style={{ fontSize: '0.95rem', color: '#1f2937' }}>
                        {result.details.description}
                      </div>
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                      Status
                    </label>
                    <div style={{ fontSize: '0.95rem', color: '#059669', fontWeight: 600 }}>
                      {result.message}
                    </div>
                  </div>
                </div>
              </div>

              {/* Storage Locations Column */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}>
                <h3 style={{
                  margin: '0 0 1.5rem 0',
                  fontSize: '1.25rem',
                  color: '#1f2937',
                  fontWeight: 600,
                  paddingBottom: '0.75rem',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  Storage Locations
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {result.record?.ipfsHash && (
                    <div style={{
                      padding: '1rem',
                      background: '#f0fdf4',
                      border: '2px solid #86efac',
                      borderRadius: '6px'
                    }}>
                      <div style={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#059669',
                        marginBottom: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ fontSize: '1.25rem' }}>📦</span>
                        IPFS Storage
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#047857', marginBottom: '0.25rem', fontWeight: 500 }}>
                          CID
                        </label>
                        <div style={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          color: '#065f46',
                          wordBreak: 'break-all',
                          background: 'white',
                          padding: '0.5rem',
                          borderRadius: '4px'
                        }}>
                          {result.record.ipfsHash}
                        </div>
                      </div>
                      {result.record.ipfsGatewayUrl && (
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', color: '#047857', marginBottom: '0.25rem', fontWeight: 500 }}>
                            Gateway URL
                          </label>
                          <a
                            href={result.record.ipfsGatewayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'block',
                              fontSize: '0.85rem',
                              color: '#059669',
                              wordBreak: 'break-all',
                              textDecoration: 'underline',
                              background: 'white',
                              padding: '0.5rem',
                              borderRadius: '4px'
                            }}
                          >
                            {result.record.ipfsGatewayUrl}
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {result.record?.blobUrl && (
                    <div style={{
                      padding: '1rem',
                      background: '#eff6ff',
                      border: '2px solid #93c5fd',
                      borderRadius: '6px'
                    }}>
                      <div style={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#2563eb',
                        marginBottom: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ fontSize: '1.25rem' }}>☁️</span>
                        Vercel Blob Storage
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#1e40af', marginBottom: '0.25rem', fontWeight: 500 }}>
                          URL
                        </label>
                        <a
                          href={result.record.blobUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'block',
                            fontSize: '0.85rem',
                            color: '#2563eb',
                            wordBreak: 'break-all',
                            textDecoration: 'underline',
                            background: 'white',
                            padding: '0.5rem',
                            borderRadius: '4px'
                          }}
                        >
                          {result.record.blobUrl}
                        </a>
                      </div>
                    </div>
                  )}

                  {result.record?.id && (
                    <div style={{
                      padding: '1rem',
                      background: '#fef3c7',
                      border: '2px solid #fbbf24',
                      borderRadius: '6px'
                    }}>
                      <div style={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#d97706',
                        marginBottom: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ fontSize: '1.25rem' }}>🗄️</span>
                        Database Record
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#92400e', marginBottom: '0.25rem', fontWeight: 500 }}>
                          Record ID
                        </label>
                        <div style={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          color: '#78350f',
                          background: 'white',
                          padding: '0.5rem',
                          borderRadius: '4px'
                        }}>
                          {result.record.id}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </>
        )}

        {/* Show Encrypted File Viewer if file is encrypted */}
        {result && result.verified && result.record?.encrypted === true && (
          <div style={{ marginTop: '1.5rem' }}>
            <EncryptedFileViewer
              verificationCode={displayVerificationCode}
              blobUrl={result.record?.blobUrl}
              record={result.record}
            />
          </div>
        )}

        {/* Show Evidence Viewer for non-encrypted files */}
        {result && result.verified && result.record?.encrypted !== true && result.record?.blobUrl && (
          <EvidenceViewer
            blobUrl={result.record.blobUrl}
            fileName={result.details?.fileName}
            verificationCode={displayVerificationCode}
          />
        )}

        {result && !result.verified && (
          <div className="alert alert-error mt-4">
            <p>{result.message}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Verification;
